<script lang="ts">
import type { CalendarFeed } from '@codot/contracts';
import { mediaApi } from '../../api/content.api';
/** One request per link for every preview on the page, kept 5 minutes. */
const cache = new Map<string, { at: number; p: Promise<CalendarFeed> }>();
function loadFeed(url: string): Promise<CalendarFeed> {
  const hit = cache.get(url);
  if (hit && Date.now() - hit.at < 5 * 60_000) return hit.p;
  const p = mediaApi.calendarPreview(url, 1);
  cache.set(url, { at: Date.now(), p });
  p.catch(() => cache.delete(url));
  return p;
}
</script>

<script setup lang="ts">
/**
 * Meeting room app preview, laid out like the player's (player-web render/meeting-room.ts): the same room rules
 * (contracts `roomStatus`), the real events of the link (read by the API), sized in container-query units.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CalendarUrlSchema, MEETING_ROOM_UNKNOWN_COLOR, meetingRoomLabels, meetingRoomPortrait, roomStatus, type MeetingRoomWidget } from '@codot/contracts';
import { errorMessage } from '../../api/http';

const props = withDefaults(defineProps<{ config: MeetingRoomWidget; zone?: string; live?: boolean }>(), { zone: 'Asia/Dubai', live: true });
const { t } = useI18n();

const feed = ref<CalendarFeed | null>(null);
const loading = ref(false);
const error = ref('');
let debounce = 0;
watch(
  () => props.config.icalUrl,
  (url) => {
    clearTimeout(debounce);
    error.value = '';
    if (!CalendarUrlSchema.safeParse(url).success) return void (feed.value = null);
    debounce = window.setTimeout(async () => {
      loading.value = true;
      try {
        feed.value = await loadFeed(url);
      } catch (e) {
        feed.value = null;
        error.value = errorMessage(e);
      } finally {
        loading.value = false;
      }
    }, 500);
  },
  { immediate: true },
);
const now = ref(Date.now());
const timer = setInterval(() => props.live && (now.value = Date.now()), 30_000);
const root = ref<HTMLElement | null>(null);
const size = ref({ w: 16, h: 9 });
const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(([e]) => e && (size.value = { w: e.contentRect.width, h: e.contentRect.height })) : null;
onMounted(() => root.value && ro?.observe(root.value));
onBeforeUnmount(() => (clearInterval(timer), clearTimeout(debounce), ro?.disconnect()));

const ar = computed(() => props.config.language === 'ar');
const L = computed(() => meetingRoomLabels(props.config.language));
const fmtTime = computed(
  () => new Intl.DateTimeFormat(ar.value ? 'ar-AE' : 'en-GB', { timeZone: props.zone, numberingSystem: ar.value ? 'arab' : 'latn', hour: '2-digit', minute: '2-digit', hour12: props.config.format === '12h' }),
);
const num = (n: number) => (ar.value ? String(n).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)]!) : String(n));
const status = computed(() => roomStatus(feed.value?.events ?? [], now.value, props.zone, props.config.maxUpcoming));
const portrait = computed(() => meetingRoomPortrait(props.config, size.value.w, size.value.h));
const name = computed(() => props.config.roomName || feed.value?.name || '');
const title = (m: { title: string }) => (props.config.hideTitles ? L.value.private : m.title);
const range = (m: { start: number; end: number }) => `${fmtTime.value.format(m.start)} – ${fmtTime.value.format(m.end)}`;
// No calendar read (bad link, still loading): neither free nor busy, as on the players.
const accent = computed(() => (!feed.value ? MEETING_ROOM_UNKNOWN_COLOR : status.value.busy ? props.config.busyColor : props.config.freeColor));
const hint = computed(() => (error.value ? error.value : loading.value && !feed.value ? t('appsCat.calLoading') : !feed.value ? t('appsCat.calPasteLink') : ''));
const listed = computed(() => props.config.maxUpcoming > 0);
</script>

<template>
  <div ref="root" class="flex h-full w-full overflow-hidden [container-type:size]" :class="portrait ? 'flex-col' : 'flex-row'" :dir="ar ? 'rtl' : 'ltr'" :style="{ background: config.background, color: config.foreground }">
    <div
      class="flex min-h-0 min-w-0 flex-col justify-between"
      :style="{ background: accent, color: '#FFFFFF', padding: 'min(6cqh, 5cqw)', flex: listed ? '0 0 58%' : '1' }"
    >
      <p class="truncate font-semibold opacity-90" :style="{ fontSize: portrait ? 'min(4.5cqh, 8cqw)' : 'min(7cqh, 4.5cqw)' }">{{ name }}</p>
      <p class="font-extrabold leading-none tracking-wide" :style="{ fontSize: portrait ? 'min(12cqh, 22cqw)' : 'min(22cqh, 13cqw)' }">{{ !feed ? '—' : status.busy ? L.busy : L.free }}</p>
      <div v-if="status.current" class="min-w-0">
        <p class="truncate font-bold" :style="{ fontSize: portrait ? 'min(4.5cqh, 8cqw)' : 'min(7.5cqh, 4.5cqw)' }">{{ title(status.current) }}</p>
        <p v-if="!config.hideTitles && config.showOrganizer && status.current.organizer" class="truncate opacity-85" :style="{ fontSize: portrait ? 'min(3cqh, 5.5cqw)' : 'min(5cqh, 3cqw)' }">{{ status.current.organizer }}</p>
        <p class="tabular-nums opacity-90" :style="{ fontSize: portrait ? 'min(3cqh, 5.5cqw)' : 'min(5cqh, 3cqw)' }">{{ range(status.current) }}</p>
        <div class="overflow-hidden rounded-full bg-white/30" :style="{ height: 'min(1.6cqh, 1.6cqw)', margin: 'min(1.5cqh, 1.5cqw) 0' }">
          <div class="h-full rounded-full bg-white" :style="{ width: `${status.current.progress * 100}%` }" />
        </div>
        <p class="font-semibold" :style="{ fontSize: portrait ? 'min(3.2cqh, 6cqw)' : 'min(5.5cqh, 3.3cqw)' }">{{ L.endsIn(status.current.minutesLeft).replace(/\d+/, num(status.current.minutesLeft)) }}</p>
      </div>
      <p v-else class="font-semibold" :style="{ fontSize: portrait ? 'min(3.6cqh, 6.5cqw)' : 'min(6cqh, 3.6cqw)' }">
        {{ hint || (status.next ? L.nextAt(fmtTime.format(status.next.start)) : L.freeRestOfDay) }}
      </p>
    </div>
    <div v-if="listed" class="min-h-0 min-w-0 flex-1 overflow-hidden" :style="{ padding: 'min(6cqh, 5cqw)' }">
      <p class="truncate font-bold" :style="{ color: accent, fontSize: portrait ? 'min(3.4cqh, 6cqw)' : 'min(6cqh, 3.5cqw)', marginBottom: 'min(2cqh, 2cqw)' }">{{ L.laterToday }}</p>
      <p v-if="feed && !status.later.length" class="opacity-70" :style="{ fontSize: portrait ? 'min(3cqh, 5.5cqw)' : 'min(5cqh, 3cqw)' }">{{ L.noMeetings }}</p>
      <div v-for="(m, i) in status.later" :key="i" class="flex items-baseline" :style="{ gap: '0.8em', fontSize: portrait ? 'min(3cqh, 5.5cqw)' : 'min(5cqh, 3cqw)', marginBottom: '0.5em' }">
        <span class="shrink-0 tabular-nums opacity-80">{{ range(m) }}</span>
        <span class="min-w-0 flex-1 truncate font-medium">{{ title(m) }}</span>
      </div>
    </div>
  </div>
</template>
